import {App} from 'vue';
import {env as envHelper} from '@/helper/env';
import * as SentryVue from "@sentry/vue";
import * as Sentry from "@sentry/capacitor";
import {useAuthStore} from '@/store/auth';
import {useGlobalEmitter} from './emitter';
import {AppInfo} from '@capacitor/app';
import {ChatSessionConfig} from "@/helper/chat/chatStore";
import {getAppInfo} from "@/helper/version";
import {onKapitelError} from "@/helper/error";
import {isLoggedIn} from "@/helper/auth";
import {consoleLog} from "@/helper/console";


let initialized = false;
const log = false

const sentryEnabled = () => {
    const flag = import.meta.env.VITE_SENTRY_ENABLED
    return flag === 'true';
}

const updateUserContext = async () => {
    if (!initialized) {
        return
    }

    if (!isLoggedIn()) {
        return
    }

    const authStore = useAuthStore()
    const currentUser = await authStore.getCurrentUser()

    // impersonation aware user identifier string
    let userId = currentUser?.readableUserIdentifier
    if (authStore.isImpersonating()) {
        userId += ' [als ' + authStore.getImpersonation()?.readableUserIdentifier + ']'
    }
    const scope = Sentry.getCurrentScope();
    scope.setUser({
        id: userId,
        username: userId,
    });

    // log real user context
    const context = {
        username: currentUser?.userName,
        readableUserIdentifier: currentUser?.readableUserIdentifier,
        roles: currentUser?.roles,
        consultant: authStore.isUserRole('consultant'),
        impersonating: authStore.isImpersonating(),
        impersonatingAs: authStore.getImpersonationUsername()
    }
    Sentry.setContext('realUser', context);

    if (log) consoleLog('SNTRY', 'set userId to %s and realUser context to %o', userId, context)
}

const updateChatSessionContext = (config: ChatSessionConfig) => {
    if (!initialized) {
        return
    }

    // log real user context
    Sentry.setContext('chatSession', {
        ...config
    });

    if (log) consoleLog('SNTRY', 'set chatSession context to %o', config)
}

const updateVersionContext = (appInfo: AppInfo) => {
    if (!initialized) {
        return
    }
    Sentry.setContext('appInfo', {
        ...appInfo
    });

    if (log) consoleLog('SNTRY', 'set appInfo context to %o', appInfo)
}

export const useSentry = (app: App) => {

    if (log) consoleLog('SNTRY', 'initializing...')

    if (!sentryEnabled()) {
        return;
    }

    const getVersionPromise = getAppInfo()

    getVersionPromise.then((appInfo) => {
        if (appInfo) {
            updateVersionContext(appInfo)
        }
    })

    const urls = [
        window.location.origin,
        import.meta.env.VITE_GRAPHQL_ENDPOINT,
        import.meta.env.VITE_REST_ENDPOINT,
        import.meta.env.VITE_AI_ENDPOINT,
        import.meta.env.VITE_LOGIN_ENDPOINT,
        import.meta.env.VITE_VERSION_ENDPOINT,
        import.meta.env.VITE_PING_ENDPOINT,
        import.meta.env.VITE_CANDIDATE_GENERATION_ENDPOINT,
    ];

    // const release= (await getVersionPromise)?.version || undefined
    Sentry.init({
        // release,
        app,
        dsn: "https://52c016c11d8e82f3c80717fa757b8b41@o4507033914245120.ingest.us.sentry.io/4507033918242816",
        environment: envHelper.getEnvironment(),
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                maskAllInputs: false,
                networkDetailAllowUrls: urls,
            }),
            SentryVue.httpClientIntegration()
        ],
        // Session Replay
        replaysSessionSampleRate: 1, // 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        
        // This option is required for capturing headers and cookies.
        sendDefaultPii: true,
    }, SentryVue.init);

    if (log) consoleLog('SNTRY', 'initialized: %o', SentryVue.getCurrentScope())

    initialized = true;

    updateUserContext();
    useGlobalEmitter().on("AppStateChanged", updateUserContext)

    useGlobalEmitter().on("ChatSessionConfigUpdated", updateChatSessionContext)

    onKapitelError(sentryCaptureException)

    //@ts-ignore
    window.SENTRY_ERROR_TEST = () => { throw new Error("Sentry Error Test") }
}

const sentryCaptureException = (exception: any) => {
    Sentry.captureException(exception)
}
